import classNames from 'classnames'
import { FocusTrap } from 'focus-trap-react'
import Image from 'next/image'
import React, { FunctionComponent, ReactNode } from 'react'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import { Portal } from '@/app/_components/Portal/Portal'
import styles from './Modal.module.css'
import Overlay from './Overlay'
import TXT from '@/messages'

export type Props = {
  header?: ReactNode | string
  body: ReactNode | string
  confirmButton?: ReactNode | string
  cancelButton?: ReactNode | string
  buttonOverrides?: ReactNode
  onConfirm?: () => void
  onClose?: () => void
  onOverlayClick?: () => void
  onModalClick?: () => void
  showCancelButton?: boolean
  showConfirmButton?: boolean
  visible: boolean
  setVisible: (visible: boolean) => void
  testId?: string
  isError?: boolean
  customBody?: boolean
  theme?: 'light' | 'dark'
}

const Modal: FunctionComponent<Props> = (props: Props) => {
  const {
    header = TXT.titles.components.modal.default,
    body,
    cancelButton = TXT.buttons.confirmStayHere,
    confirmButton = TXT.buttons.confirmStartOver,
    buttonOverrides = undefined,
    showConfirmButton = true,
    showCancelButton = true,
    visible,
    setVisible,
    onConfirm = undefined,
    onClose = undefined,
    onOverlayClick = undefined,
    onModalClick = undefined,
    testId = undefined,
    isError = false,
    customBody = false,
    theme = 'light',
  } = props

  if (!onConfirm && !buttonOverrides && !customBody) {
    throw new Error('onConfirm required')
  }

  return (
    <Portal selector={`#${PORTAL_SELECTORS.modal}`}>
      {visible && (
        <FocusTrap>
          <div>
            {/* Note overlay click will not work if outside of FocusTrap */}
            <Overlay testId="modal-overlay" onClick={onOverlayClick} />
            <div
              data-theme={theme}
              aria-modal="true"
              aria-labelledby="modal-header"
              aria-describedby="modal-body"
              role="alertdialog"
              onClick={onModalClick}
              className={
                classNames({
                  [styles.ModalError]: isError,
                  [styles.ModalGeneral]: !isError,
                  [styles.InterruptModal]: customBody,
                }, styles.ModalContainer)
              }
              {...(testId ? { 'data-testid': testId } : {})}
            >
              <button
                className={styles.CloseIcon}
                type="button"
                onClick={() => {
                  onClose?.()
                  setVisible(false)
                }}
                aria-label={TXT.buttons.ariaCloseModal}
              >
                <Image
                  width={24}
                  height={24}
                  src="/static/svg/close.svg"
                  alt="close"
                  data-testid="modal-close-icon"
                />
              </button>
              {!customBody
                ? (
                  <div>
                    <div className={styles.ModalHeading}>
                      <div id="modal-header" className={styles.ModalHeader} data-testid="modal-header">{header}</div>
                    </div>
                    <div className={styles.ModalBody} data-testid="modal-body">
                      {body}
                    </div>
                    <div className={styles.ButtonContainer}>
                      {buttonOverrides
                        ? (
                          <>{buttonOverrides}</>
                        )
                        : (
                          <>
                            {showConfirmButton && (
                              <button
                                type="button"
                                data-testid="modal-confirm"
                                onClick={onConfirm}
                                className={styles.Confirm}
                              >
                                {confirmButton}
                              </button>
                            )}
                            {showCancelButton && (
                              <button
                                type="button"
                                data-testid="modal-cancel"
                                onClick={() => {
                                  setVisible(false)
                                  onClose?.()
                                }}
                                className={styles.Cancel}
                              >
                                {cancelButton}
                              </button>
                            )}
                          </>
                        )}
                    </div>
                  </div>
                )
                : body}
            </div>
          </div>
        </FocusTrap>
      )}
    </Portal>
  )
}

export default Modal
